import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getNodeAlert,
  getNodeCameras,
  getNodeUsers,
  getNodeModelEventImage,
  acknowledgeNodeAlert,
  unacknowledgeNodeAlert,
  assignNodeAlert,
  resolveNodeAlert,
  cancelNodeAlert,
} from '../services/api.service';
import { tableToModel } from '../api/hexAlerts.core';
import {
  AlertActions,
  AlertActivity,
  AlertInfoGrid,
  ConfidenceMeter,
  ActionModal,
  AssignModal,
  fmtTime,
  alertName,
} from './AlertsPage';
import {
  AlertStatusTag,
  PageTitle,
  SnapshotGallery,
  SeverityTag,
  snapshotTypeLabel,
  snapshotTypeRank,
} from '@trinetra/shared';

/**
 * AlertDetailView — one alert, everything about it: info, snapshots streamed from the node,
 * lifecycle status + activity log, and the same lifecycle actions as the list.
 *
 * Deliberately free of page chrome (no back button, no outer padding) so the SAME component
 * serves the routed `/alerts/:id` page below AND the modal the hexagon board opens — which is
 * the point: an operator watching a board shouldn't have to leave it to read an alert, and the
 * two views must never drift apart into separate implementations of "alert detail".
 */
export function AlertDetailView({
  alertId,
  className = '',
  showTitle = true,
  titleActions = null,
  nodeId: nodeIdProp,
}: {
  alertId: string;
  className?: string;
  /**
   * The routed page has no chrome of its own, so it needs the alert's name at the top. A modal
   * already spells it in the dialog header, and repeating it there cost a whole band of the
   * one screen the record is supposed to fit inside.
   */
  showTitle?: boolean;
  /** Extra buttons beside the title in the app's title bar (the routed page's Back). */
  titleActions?: ReactNode;
  /**
   * Which site this alert belongs to. Defaults to the selected one, which is right everywhere
   * a site is selected — and wrong on the all-sites alert board, where NO site is selected and
   * the alert being opened may come from any of them. That board knows the answer (its camera
   * ids are site-qualified) and passes it, so the same detail view serves both.
   */
  nodeId?: string;
}) {
  const { selectedNode } = useNode();
  const { user, hasPermission } = useAuth();
  const canManageAlerts = hasPermission('alerts.edit'); // backend also enforces
  const nodeId = nodeIdProp || selectedNode?.nodeId || '';
  const uid = user?.id || null;
  const uname = user?.name || user?.email || null;

  const [alert, setAlert] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [cameraMap, setCameraMap] = useState<Record<string, string>>({});
  const [users, setUsers] = useState<any[]>([]);
  const [actionKind, setActionKind] = useState<'resolve' | 'cancel' | null>(null);
  const [actionError, setActionError] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [snapshots, setSnapshots] = useState<{ type: string; url: string }[]>([]);
  const [snapsLoading, setSnapsLoading] = useState(false);

  const userMap = Object.fromEntries(users.map((u) => [u.id, u.full_name || u.name || u.email || u.id]));

  const load = useCallback(async () => {
    if (!nodeId || !alertId) { setLoading(false); return; }
    setLoading(true);
    try {
      setAlert(await getNodeAlert(nodeId, alertId));
    } catch (e) {
      console.error('Failed to load alert:', e);
      setAlert(null);
    } finally {
      setLoading(false);
    }
  }, [nodeId, alertId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!nodeId) return;
    getNodeCameras(nodeId)
      .then((cams: any) => {
        const arr = Array.isArray(cams) ? cams : cams?.data || [];
        setCameraMap(Object.fromEntries(arr.map((c: any) => [c.id, c.name])));
      })
      .catch(() => {});
    getNodeUsers(nodeId)
      .then((list: any[]) => setUsers(Array.isArray(list) ? list : []))
      .catch(() => setUsers([]));
  }, [nodeId]);

  // Fetch the alert's snapshot images through the tunnel image proxy (same mechanism the
  // hexagon preview uses). Node image_urls aren't directly reachable from the cloud, so each
  // image is streamed as base64→blob via getNodeModelEventImage(model, event_id, imageType).
  useEffect(() => {
    const urls = alert?.image_urls && typeof alert.image_urls === 'object' ? alert.image_urls : {};
    // Fetch order follows display order, so the frame shown first is also the first one
    // streamed through the tunnel.
    const types = Object.keys(urls).sort((a, b) => snapshotTypeRank(a) - snapshotTypeRank(b));
    const model = tableToModel(alert?.source_table);
    if (!alert || !nodeId || !alert.event_id || !model || types.length === 0) {
      setSnapshots([]);
      setSnapsLoading(false);
      return;
    }
    let cancelled = false;
    const made: string[] = [];
    setSnapshots([]);
    setSnapsLoading(true);
    (async () => {
      for (const type of types) {
        try {
          const blob: any = await getNodeModelEventImage(nodeId, model, alert.event_id, type);
          if (cancelled) return;
          // A tunnel 404/timeout still resolves as a (JSON/text) Blob — only keep real images.
          if (blob && (!blob.type || blob.type.startsWith('image/'))) {
            const url = URL.createObjectURL(blob);
            made.push(url);
            // Published one at a time: the wide shot appears while the crops are still in
            // flight. Collecting the lot and setting it at the end meant the record sat on
            // "Loading snapshots…" for three or four tunnel round trips — and one frame that
            // hung or 404'd hid the frames that had already arrived.
            setSnapshots((prev) => [...prev, { type, url }]);
            setSnapsLoading(false);
          }
        } catch { /* skip this image */ }
      }
      if (cancelled) { made.forEach((u) => URL.revokeObjectURL(u)); return; }
      setSnapsLoading(false);
    })();
    return () => { cancelled = true; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [alert, nodeId]);

  // Every lifecycle call returns the updated alert row — apply it so the status and
  // the remaining actions update immediately; refetch if the shape is unexpected,
  // and surface the failure instead of only logging it.
  const runAction = async (fn: () => Promise<any>) => {
    setActionError('');
    try {
      const updated = await fn();
      if (updated && updated.id) setAlert(updated); else await load();
    } catch (e: any) {
      console.error(e);
      setActionError(e?.response?.data?.message || e?.message || 'Action failed');
    }
  };

  const doAck = () => runAction(() => acknowledgeNodeAlert(nodeId, alertId, uid, uname));
  const doUnack = () => runAction(() => unacknowledgeNodeAlert(nodeId, alertId, uid, uname));
  const doAssign = (assigneeId: string, assigneeName: string) => runAction(() => assignNodeAlert(nodeId, alertId, assigneeId, uid, assigneeName, uname));
  const doResolve = (note: string) => runAction(() => resolveNodeAlert(nodeId, alertId, uid, note, uname));
  const doCancel = (reason: string) => runAction(() => cancelNodeAlert(nodeId, alertId, uid, reason, uname));

  const urls = alert?.image_urls && typeof alert.image_urls === 'object' ? alert.image_urls : {};
  const imgs = Object.values(urls).filter(Boolean) as string[];

  if (loading) {
    return (
      <div className={`p-6 space-y-3 ${className}`}>
        {[1, 2, 3].map((i) => <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />)}
      </div>
    );
  }
  if (!alert) {
    return (
      <div className={`py-16 text-center text-muted-foreground text-sm ${className}`}>
        Alert not found{!nodeId ? ' — select a site from the sidebar.' : '.'}
      </div>
    );
  }

  return (
    <>
    <div className={`flex min-h-full flex-col ${className}`}>
      {/* Only where nothing above the record already says which alert this is. */}
      {showTitle && (
        <PageTitle title={alertName(alert)}>
          <SeverityTag severity={alert.severity} />
          <AlertStatusTag status={alert.status} />
          <button
            onClick={load}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
          {titleActions}
        </PageTitle>
      )}

      {/* Snapshots beside the facts, not above them: the record is read at the modal's full
          width now, and stacked it took two screens to say what fits on one. Both columns
          collapse to one below xl, where there is no width to split. */}
      <div className="grid flex-1 gap-6 px-6 py-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          {/* Snapshots streamed from the node through the tunnel image proxy. */}
          {snapshots.length > 0 ? (
            /* Frames down the left, the chosen one large on the right — and a small crop is
               scaled up instead of sitting at its own 64px in a black rectangle. Same viewer
               as the event record. See SnapshotGallery. */
            <SnapshotGallery
              images={snapshots.map((s) => ({ url: s.url, label: snapshotTypeLabel(s.type) }))}
              // ← → stay with the alerts list stepping through RECORDS; ↑ ↓ move between this
              // alert's frames.
              arrowKeys="updown"
              frameClassName="h-[38vh]"
              badge={fmtTime(alert.event_timestamp)}
              overlay={alert.confidence != null ? <ConfidenceMeter value={alert.confidence} /> : undefined}
            />
          ) : (
            <div className="flex h-[38vh] items-center justify-center rounded-lg border border-border bg-muted/30 text-center text-[13px] text-muted-foreground">
              {imgs.length > 0 && snapsLoading
                ? 'Loading snapshots…'
                : imgs.length > 0
                  ? 'Snapshots unavailable (site unreachable).'
                  : 'No snapshots for this alert'}
            </div>
          )}
        </div>

        <div className="min-w-0">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Alert Information</h3>
            {/* Severity and status ride the section heading rather than a band of their own:
                in the modal the name is already in the dialog header, and these two chips are
                the only part of that band the reader could not have got from it. */}
            {!showTitle && (
              <>
                <SeverityTag severity={alert.severity} />
                <AlertStatusTag status={alert.status} />
                <span className="flex-1" />
                <button onClick={load} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted" title="Refresh">
                  <ArrowPathIcon className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
          <AlertInfoGrid
            items={[
              { label: 'Event', value: alert.event_type },
              { label: 'Alert name', value: alertName(alert) },
              { label: 'Confidence', value: alert.confidence != null ? `${(alert.confidence * 100).toFixed(0)}%` : '—' },
              { label: 'Camera', value: cameraMap[alert.camera_id] || alert.camera_id },
              { label: 'Detected', value: fmtTime(alert.event_timestamp) },
              { label: 'Assignee', value: alert.assigned_to ? (userMap[alert.assigned_to] || 'Assigned') : 'Unassigned' },
              { label: 'Acknowledged by', value: alert.acknowledged_by ? (userMap[alert.acknowledged_by] || 'Yes') : '—' },
              // The summary is the sentence the model wrote about what it saw — a fact of the
              // alert, and read as one now instead of as a subtitle nobody scrolled back to.
              !!alert.summary && { label: 'Summary', value: alert.summary, wide: true },
              !!alert.resolution_note && { label: 'Resolution note', value: alert.resolution_note, wide: true },
              !!alert.cancel_reason && { label: 'Cancel reason', value: alert.cancel_reason, wide: true },
            ]}
          />

          {actionError && (
            <p className="mt-4 rounded-lg bg-red-50 dark:bg-red-900/20 px-3 py-2 text-[12px] text-destructive">{actionError}</p>
          )}

          <AlertActivity nodeId={nodeId} alert={alert} alertId={alert.id} userMap={userMap} currentUser={{ id: uid, name: uname }} />
        </div>
      </div>

      {canManageAlerts && (
        <AlertActions
          alert={alert}
          userMap={userMap}
          onAck={doAck}
          onReopen={doUnack}
          onAssign={() => setAssigning(true)}
          onResolve={() => setActionKind('resolve')}
          onCancel={() => setActionKind('cancel')}
        />
      )}
    </div>

    {/* Outside the card: these are their own overlays, not part of the record's layout. */}
    {assigning && (
      <AssignModal
        users={users}
        currentUser={{ id: uid, name: uname }}
        onClose={() => setAssigning(false)}
        onConfirm={(assigneeId: string, assigneeName: string) => { doAssign(assigneeId, assigneeName); setAssigning(false); }}
      />
    )}
    {actionKind && (
      <ActionModal
        kind={actionKind}
        onClose={() => setActionKind(null)}
        onConfirm={(value: string) => {
          if (actionKind === 'resolve') doResolve(value); else doCancel(value);
          setActionKind(null);
        }}
      />
    )}
    </>
  );
}

/**
 * AlertDetailPage — a routed single-alert view (/alerts/:id), reached from the alerts list and
 * from anywhere that deep-links an alert. The hexagon board opens AlertDetailView in a modal
 * instead, so the operator keeps the board.
 */
export default function AlertDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  return (
    <div>
      <AlertDetailView
        alertId={id}
        className="rounded-xl border border-border bg-card shadow-card overflow-hidden"
        titleActions={(
          <button
            onClick={() => navigate('/alerts')}
            className="flex h-8 items-center gap-1 rounded-lg border border-border px-2.5 text-[12px] text-foreground hover:bg-muted transition-colors"
          >
            <ArrowLeftIcon className="h-3.5 w-3.5" /> Alerts
          </button>
        )}
      />
    </div>
  );
}
